import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';
import { Pill } from '../../componentes/ui/Pill.js';

interface Operadora {
  id: string;
  nome: string;
}

interface Previa {
  cidr: string;
  versao: 'V4' | 'V6';
  totalEnderecos: string;
  zonas: string[];
  classless: boolean;
  erro?: string;
}

/**
 * Cadastro de bloco.
 *
 * A prévia é o ponto central desta tela. Um `/22` gera quatro zonas e um
 * prefixo IPv6 gera nomes que ninguém confere de cabeça — mostrar o que será
 * criado antes de criar evita descobrir o engano depois, quando desfazer já
 * significa mexer em zonas que estão no ar.
 */
export function ModalImportarCidr({
  aoFechar,
  aoCriar,
  operadoraPadrao,
}: {
  aoFechar: () => void;
  aoCriar: () => void;
  operadoraPadrao?: string;
}) {
  const [cidr, setCidr] = useState('');
  const [operadoraId, setOperadoraId] = useState(operadoraPadrao ?? '');
  const [observacao, setObservacao] = useState('');
  const [previa, setPrevia] = useState<Previa | null>(null);

  const { data: operadoras = [] } = useQuery({
    queryKey: ['operadoras'],
    queryFn: () => api.get<Operadora[]>('/operadoras'),
  });

  // A prévia é calculada no servidor, com o mesmo código que grava. Repetir a
  // matemática de CIDR no navegador criaria duas verdades sobre quantas zonas
  // um bloco tem — e elas divergiriam justamente nos casos difíceis.
  useEffect(() => {
    if (cidr.trim().length < 7) {
      setPrevia(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .post<Previa>('/blocos/previa', { cidr: cidr.trim() })
        .then(setPrevia)
        .catch((err: unknown) =>
          setPrevia({
            cidr: cidr.trim(),
            versao: 'V4',
            totalEnderecos: '0',
            zonas: [],
            classless: false,
            erro: err instanceof ApiError ? err.mensagem : 'Bloco inválido',
          }),
        );
    }, 350);
    return () => clearTimeout(t);
  }, [cidr]);

  const criar = useMutation({
    mutationFn: () =>
      api.post('/blocos', {
        cidr: cidr.trim(),
        operadoraId: operadoraId || undefined,
        observacao: observacao.trim() || undefined,
      }),
    onSuccess: () => {
      aoCriar();
      aoFechar();
    },
  });

  const valido = !!previa && !previa.erro && !!operadoraId;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="largo"
      titulo="Importar bloco CIDR"
      descricao="As zonas reversas são calculadas e criadas no PowerDNS automaticamente."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={!valido}
          >
            Importar bloco
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <Rotulo>Bloco CIDR</Rotulo>
            <input
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              autoFocus
              spellCheck={false}
              placeholder="200.150.30.0/24"
              className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[14px] focus:border-marca focus:outline-none"
            />
            <span className="mt-1 block text-[12px] text-texto-suave">
              IPv4 ou IPv6. Endereços fora da rede são normalizados.
            </span>
          </label>

          <label className="block">
            <Rotulo>Operadora</Rotulo>
            <select
              value={operadoraId}
              onChange={(e) => setOperadoraId(e.target.value)}
              className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
            >
              <option value="">Selecione…</option>
              {operadoras.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
            {operadoras.length === 0 && (
              <span className="mt-1 block text-[12px] text-aviso">
                Cadastre uma operadora antes — o bloco precisa de um dono.
              </span>
            )}
          </label>
        </div>

        {previa?.erro && (
          <p className="flex items-start gap-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            <AlertTriangle size={15} className="mt-px shrink-0" />
            {previa.erro}
          </p>
        )}

        {previa && !previa.erro && (
          <div className="rounded-card border border-borda bg-fundo p-4">
            <p className="mb-3 flex items-center gap-2 text-[13px] font-extrabold">
              <CheckCircle2 size={15} className="text-sucesso" />
              Será criado
            </p>

            <dl className="grid gap-2 text-[13px] sm:grid-cols-3">
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-[0.5px] text-texto-suave">
                  Bloco
                </dt>
                <dd className="font-mono font-semibold">{previa.cidr}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-[0.5px] text-texto-suave">
                  Endereços
                </dt>
                <dd className="font-mono font-semibold">
                  {formatarTotal(previa.totalEnderecos)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-[0.5px] text-texto-suave">
                  Versão
                </dt>
                <dd>
                  <Pill tom={previa.versao === 'V6' ? 'ipv6' : 'info'} mono>
                    {previa.versao === 'V6' ? 'IPv6' : 'IPv4'}
                  </Pill>
                </dd>
              </div>
            </dl>

            <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.5px] text-texto-suave">
              {previa.zonas.length} zona(s) reversa(s)
            </p>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {previa.zonas.map((z) => (
                <li
                  key={z}
                  className="rounded-chip bg-superficie px-2 py-1 font-mono text-[11.5px]"
                >
                  {z}
                </li>
              ))}
            </ul>

            {previa.classless && (
              // Avisar antes de cadastrar: descobrir depois que o bloco precisa
              // de RFC 2317 no upstream custa uma conversa com o cliente.
              <p className="mt-3 rounded-chip bg-aviso-fundo px-3 py-2 text-[12px] leading-snug text-aviso">
                Bloco menor que /24. O reverso só resolve com delegação classless
                (RFC 2317) configurada por quem controla o /24 que o contém.
              </p>
            )}
          </div>
        )}

        <label className="block">
          <Rotulo>Observação</Rotulo>
          <input
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Opcional — contrato, data de alocação, etc."
            className="h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
          />
        </label>

        {criar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(criar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Um /48 IPv6 tem 2^80 endereços: notação científica em vez de 25 dígitos. */
function formatarTotal(total: string): string {
  const n = Number(total);
  if (!Number.isFinite(n)) return total;
  if (n > 1e12) return n.toExponential(2);
  return n.toLocaleString('pt-BR');
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
      {children}
    </span>
  );
}
